<template>
  <v-container fluid class="pa-4">
    <!-- Header Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="d-flex align-center mb-2">
          <v-avatar size="56" class="mr-4 primary">
            <v-icon size="32" color="white">mdi-folder-download</v-icon>
          </v-avatar>
          <div>
            <h1 class="display-1 mb-1 font-weight-bold">Resources</h1>
            <p class="subtitle-1 grey--text text--darken-1 mb-0">
              Download helpful documents and resources
            </p>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Language Filter -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedLanguage"
          :items="languages"
          label="Language"
          outlined
          dense
          @change="loadResources"
        ></v-select>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-card v-if="loading" class="mb-4" elevation="2">
      <v-card-text class="pa-6">
        <div class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            width="6"
            class="mb-4"
          ></v-progress-circular>
          <div class="title">Loading resources...</div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Error State -->
    <v-card v-else-if="error" class="mb-4" elevation="2" outlined>
      <v-alert type="error" prominent border="left" colored-border>
        <v-alert-title class="headline">Error</v-alert-title>
        <div class="mt-2">{{ error }}</div>
      </v-alert>
    </v-card>

    <!-- Empty State -->
    <v-card v-else-if="resources.length === 0" class="mb-4" elevation="2" outlined>
      <v-card-text class="pa-6 text-center">
        <v-icon size="80" color="grey lighten-1" class="mb-4">mdi-folder-off</v-icon>
        <h2 class="headline mb-2">No Resources Available</h2>
        <p class="body-1 grey--text">
          No resources are currently available in {{ selectedLanguage === 'en' ? 'English' : 'Spanish' }}.
        </p>
      </v-card-text>
    </v-card>

    <!-- Resources List -->
    <v-card v-else elevation="2">
      <v-data-table
        v-if="$vuetify.breakpoint.mdAndUp"
        :headers="headers"
        :items="resources"
        :items-per-page="10"
        class="elevation-0"
        :loading="loading"
      >
        <template v-slot:item.title="{ item }">
          <div class="d-flex align-center">
            <v-icon class="mr-2" color="primary">mdi-file-document</v-icon>
            <strong>{{ item.title }}</strong>
          </div>
        </template>
        <template v-slot:item.language="{ item }">
          <v-chip x-small :color="item.language === 'en' ? 'blue' : 'green'" text-color="white">
            {{ item.language === 'en' ? 'English' : 'Spanish' }}
          </v-chip>
        </template>
        <template v-slot:item.fileSize="{ item }">
          {{ formatFileSize(item.fileSize) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            color="primary"
            small
            outlined
            class="mr-2"
            @click="viewResource(item)"
          >
            <v-icon left small>mdi-open-in-new</v-icon>
            View
          </v-btn>
          <v-btn
            color="primary"
            small
            outlined
            @click="downloadResource(item)"
            :loading="downloading === item._id"
          >
            <v-icon left small>mdi-download</v-icon>
            Download
          </v-btn>
        </template>
      </v-data-table>

      <!-- Mobile: clean stacked cards instead of the cramped native table -->
      <div v-else class="pa-3">
        <v-card
          v-for="item in resources"
          :key="item._id"
          outlined
          class="mb-3"
        >
          <v-card-text class="pb-2">
            <div class="d-flex align-start mb-2">
              <v-icon color="primary" class="mr-2 mt-1">mdi-file-document</v-icon>
              <span class="font-weight-bold body-1">{{ item.title }}</span>
            </div>
            <div class="d-flex flex-wrap mb-2" style="gap: 8px;">
              <v-chip x-small :color="item.language === 'en' ? 'blue' : 'green'" text-color="white">
                {{ item.language === 'en' ? 'English' : 'Spanish' }}
              </v-chip>
            </div>
            <div class="caption grey--text">{{ formatFileSize(item.fileSize) }}</div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-btn text small color="primary" @click="viewResource(item)">
              <v-icon left small>mdi-open-in-new</v-icon>View
            </v-btn>
            <v-btn text small color="primary" :loading="downloading === item._id" @click="downloadResource(item)">
              <v-icon left small>mdi-download</v-icon>Download
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Resources',
  created() {
    console.log('📚 Resources component created');
  },
  data() {
    console.log('📚 Resources component data() called');
    return {
      resources: [],
      loading: true,
      error: null,
      selectedLanguage: 'en',
      languages: [
        { text: 'English', value: 'en' },
        { text: 'Spanish', value: 'es' }
      ],
      downloading: null,
      headers: [
        { text: 'Title', value: 'title', sortable: true },
        { text: 'Language', value: 'language', sortable: true },
        { text: 'File Size', value: 'fileSize', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'end' }
      ]
    };
  },
  mounted() {
    console.log('📚 Resources component mounted');
    console.log('📚 Initial state:', {
      loading: this.loading,
      resources: this.resources,
      error: this.error,
      selectedLanguage: this.selectedLanguage
    });
    this.loadResources();
  },
  methods: {
    async loadResources() {
      this.loading = true;
      this.error = null;
      try {
        console.log(`📚 Loading resources for language: ${this.selectedLanguage}`);
        const response = await axios.get('/api/resources', {
          params: {
            language: this.selectedLanguage
          }
        });
        console.log('📚 Resources response:', response.data);
        this.resources = response.data || [];
        console.log(`✅ Loaded ${this.resources.length} resources`);
      } catch (error) {
        console.error('❌ Error loading resources:', error);
        console.error('❌ Error response:', error.response);
        this.error = (error.response && error.response.data && error.response.data.error) || 'Failed to load resources';
      } finally {
        this.loading = false;
      }
    },
    // Open the resource inline in a new browser tab. Auth is cookie-based, so
    // the session rides along with the request.
    viewResource(resource) {
      window.open(`/api/resources/${resource._id}/download?inline=1`, '_blank', 'noopener');
    },
    async downloadResource(resource) {
      this.downloading = resource._id;
      try {
        const response = await axios.get(`/api/resources/${resource._id}/download`, {
          responseType: 'blob'
        });
        
        // Create blob and download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', resource.originalFileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading resource:', error);
        if (this.$toast && this.$toast.error) {
          this.$toast.error('Failed to download resource');
        }
      } finally {
        this.downloading = null;
      }
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    getFileType(mimeType) {
      if (!mimeType) return 'Unknown';
      const parts = mimeType.split('/');
      return parts[parts.length - 1].toUpperCase();
    }
  }
};
</script>

<style scoped>
.fill-height {
  height: 100%;
}
</style>
